/* New-look application shell: its own Router, glassy nav, footer,
   and per-page SEO. Rendered when the legacy-design feature flag is OFF. */
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import SEO from 'components/SEO';
import { NavBar, Footer } from './chrome';
import { CookieBanner } from './consent';
import { buildJsonLd } from './seoData';
import { View } from './nav';
import './styles/tokens.css';

const HomePage = lazy(() => import('./pages/HomePage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const PortfolioPage = lazy(() => import('./pages/PortfolioPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const TableFinchPage = lazy(() => import('./pages/TableFinchPage'));
const NyasaPage = lazy(() => import('./pages/NyasaPage'));
const CompassoPage = lazy(() => import('./pages/CompassoPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));

const SITE_IMAGE = 'https://femtowareinfotech.com/og/home.jpg';

interface PageMeta {
  path: string;
  title: string;
  description: string;
  /** Link-preview image (1200×630) — defaults to the site-wide one. */
  image?: string;
}

const PAGE_META: Record<string, PageMeta> = {
  home: {
    path: '/',
    title: 'AI Software & Platforms with Intelligent Agents',
    description:
      'Femtoware Infotech builds AI products where intelligent agents do real work — NYASA writes code, Compasso plans routes, Table Finch runs restaurants — and builds the same for your business.',
  },
  services: {
    path: '/services',
    title: 'Services',
    description:
      'Software built with NYASA, AI agents for operations, and optimisation & data — three ways Femtoware puts AI to work, with security, governance, audit and compliance built in.',
  },
  portfolio: {
    path: '/portfolio',
    title: 'Products',
    description:
      'Femtoware products in production — NYASA (AI software delivery), Compasso (route optimisation for transport and delivery) and Table Finch (the all-in-one restaurant operating system with a built-in AI receptionist).',
  },
  about: {
    path: '/about',
    title: 'About',
    description:
      'Femtoware Infotech LLP — a Kerala-based company building AI software that does real work, with intelligence engineered in from the start. Faster. Smarter. Precise.',
  },
  compasso: {
    path: '/compasso',
    image: 'https://femtowareinfotech.com/og/compasso.jpg',
    title: 'Compasso — Route Optimisation for Transport, Delivery and Field Teams',
    description:
      'Compasso by Femtoware plans optimal vehicle routes in seconds — road-true, rule-checked, with live-traffic ETAs — on self-hosted infrastructure. Up to ~90% lower routing data spend in a client deployment.',
  },
  nyasa: {
    path: '/nyasa',
    image: 'https://femtowareinfotech.com/og/nyasa.jpg',
    title: 'NYASA by Femtoware — AI Agents that Turn Tickets into Pull Requests',
    description:
      'NYASA plans each change, waits for your approval, writes the code, runs the tests and opens a pull request on your GitHub — with per-person access, AI budgets and a built-in security review. Early access open.',
  },
  tablefinch: {
    path: '/table-finch',
    image: 'https://femtowareinfotech.com/og/table-finch.jpg',
    title: 'Table Finch — The All-in-one Restaurant Operating System',
    description:
      'Table Finch by Femtoware runs your whole restaurant from one place — AI phone and WhatsApp receptionist, reservations, live floor plan, waiter app, kitchen display, POS, QR menu, inventory, staff and analytics.',
  },
  privacy: {
    path: '/privacy',
    title: 'Privacy Policy',
    description:
      'How Femtoware Infotech handles personal data collected through femtowareinfotech.com.',
  },
  contact: {
    path: '/contact',
    title: 'Contact',
    description:
      'Tell us what you’re building. Our team responds with a plan, a timeline and a team — usually within 48 hours.',
  },
};

const Seo = ({ page }: { page: keyof typeof PAGE_META }) => {
  const meta = PAGE_META[page];
  return (
    <SEO
      title={meta.title}
      description={meta.description}
      path={meta.path}
      image={meta.image ?? SITE_IMAGE}
      type='website'
      jsonLd={buildJsonLd(page as View, meta.title || 'Home')}
    />
  );
};

const ScrollManager = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);
  return null;
};

const NewLookLayout = () => (
  <div className='fw-newlook'>
    <NavBar />
    <ScrollManager />
    <main className='fw-page'>
      <Suspense fallback={<div style={{ minHeight: '60vh' }} />}>
        <Routes>
          <Route
            path='/'
            element={
              <>
                <Seo page='home' />
                <HomePage />
              </>
            }
          />
          <Route
            path='/services'
            element={
              <>
                <Seo page='services' />
                <ServicesPage />
              </>
            }
          />
          <Route
            path='/portfolio'
            element={
              <>
                <Seo page='portfolio' />
                <PortfolioPage />
              </>
            }
          />
          <Route
            path='/about'
            element={
              <>
                <Seo page='about' />
                <AboutPage />
              </>
            }
          />
          <Route
            path='/compasso'
            element={
              <>
                <Seo page='compasso' />
                <CompassoPage />
              </>
            }
          />
          <Route
            path='/nyasa'
            element={
              <>
                <Seo page='nyasa' />
                <NyasaPage />
              </>
            }
          />
          <Route
            path='/table-finch'
            element={
              <>
                <Seo page='tablefinch' />
                <TableFinchPage />
              </>
            }
          />
          {/* Old product name — keeps stray /voxli links landing on the right page. */}
          <Route path='/voxli' element={<Navigate to='/table-finch' replace />} />
          <Route
            path='/contact'
            element={
              <>
                <Seo page='contact' />
                <ContactPage />
              </>
            }
          />
          <Route
            path='/privacy'
            element={
              <>
                <Seo page='privacy' />
                <PrivacyPage />
              </>
            }
          />
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </Suspense>
    </main>
    <Footer />
    <CookieBanner />
  </div>
);

const NewLookApp = () => (
  <BrowserRouter>
    <NewLookLayout />
  </BrowserRouter>
);

export default NewLookApp;
